import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';

const ImageViewerModal = ({ isOpen, onClose, images }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const previousImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex flex-col items-center justify-center"
        onClick={onClose}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-0 left-0 right-0 flex justify-between items-center p-4 text-white z-10"
        >
          <div className="flex items-center gap-2">
            <ImageIcon size={20} className="text-white/70" />
            <span className="text-sm">Image {currentImageIndex + 1} of {images.length}</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-all"
          >
            <X size={24} />
          </button>
        </motion.div>

        {/* Image Container */}
        <div className="relative w-full h-full flex items-center justify-center p-20">
          {/* Navigation Buttons */}
          <button
            onClick={(e) => { e.stopPropagation(); previousImage(); }}
            className="absolute left-8 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-all"
          >
            <ChevronLeft size={24} className="text-white" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-8 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-all"
          >
            <ChevronRight size={24} className="text-white" />
          </button>

          {/* Image */}
          <motion.img
            key={currentImageIndex}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", duration: 0.5 }}
            src={images[currentImageIndex]}
            alt={`Ticket Image ${currentImageIndex + 1}`}
            className="max-h-[80vh] max-w-[80vw] object-contain rounded-lg shadow-2xl"
          />
        </div>

        {/* Thumbnails */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-0 left-0 right-0 flex justify-center gap-2 p-4 bg-gradient-to-t from-black/50 to-transparent"
        >
          {images.map((img, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
              className={`relative rounded-lg overflow-hidden transition-all ${
                currentImageIndex === index ? 'ring-2 ring-white scale-110' : 'opacity-50 hover:opacity-100'
              }`}
            >
              <img
                src={img}
                alt={`Thumbnail ${index + 1}`}
                className="w-16 h-16 object-cover"
              />
            </button>
          ))}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImageViewerModal;
